import { Injectable } from '@angular/core';
import { GlobalVariable } from 'src/app/global';
import { Util } from 'src/app/utils/utils';
import { RolesPartidosService } from '../roles-partidos/roles-partidos.service';

declare const PDFDocument:any;
declare const blobStream:any;

/* Dibujo del PDF del rol de partidos (versiones 1, 2 y 3). Vivia dentro de RolPartidosComponent y se
   paso aca para que la configuracion del campeonato pueda mostrar una vista previa en vivo con el
   mismo codigo que despues imprime el rol. El servicio solo dibuja: que se hace con el documento
   (abrirlo, convertirlo a imagen, mostrarlo en la vista previa) lo decide quien lo llama.
   Los bloques de dibujo son los mismos de antes, sin cambios de maquetacion. */
@Injectable({
  providedIn: 'root'
})
export class RolPartidoPdfService {

  rest_server=GlobalVariable.SERVER_URL;
  //usuario de la generacion en curso: la cabecera usa el logo de su empresa
  usuario:any;
  //logos ya descargados, solo cuando la generacion lo pide (la vista previa regenera muchas veces)
  cache_imagenes:Map<string,Promise<any>>=new Map();
  usar_cache_imagenes:boolean=false;
  fuentes_fixture:any=null;
  /* Cuanto tiñe el color del campeonato a la cancha de fondo: mas bajo deja ver mas la foto,
     mas alto le da mas color de marca. */
  intensidad_tinte_fixture:number=0.40;
  /* Opacidad de la tarjeta: 1 = opaca, mas bajo deja ver mas la cancha. El oscurecimiento del
     color se ajusta solo para que el texto siga siendo legible. */
  opacidad_tarjeta_fixture:number=0.80;
  /* El fixture V2 se arma sobre un lienzo de 612x765 pt, la proporcion 4:5 que Facebook e Instagram
     muestran entera en el feed (1080x1350 px una vez convertido a PNG). Los partidos se reparten en
     tantas paginas como haga falta y se estiran hasta llenar el alto disponible: cuantos menos
     entran en una pagina, mas grandes salen la tarjeta y con ella todas las letras. */
  ancho_fixture:number=612;
  alto_fixture:number=765;
  y_inicio_fixture:number=116;
  margen_inferior_fixture:number=18;
  paso_minimo_fixture:number=90;
  paso_maximo_fixture:number=150;
  /* Silabeo del español, para cortar los nombres de equipo largos al final de la linea. Los grupos
     inseparables incluyen los digrafos (ch, ll, rr), las consonantes con l/r que abren silaba
     (bl, br, cl, ...) y qu/gu, que suenan como una sola consonante ante e/i. */
  vocales_fuertes_fixture:string="aeoáéó";
  vocales_debiles_fixture:string="iuü";
  vocales_debiles_acentuadas_fixture:string="íú";
  grupos_inseparables_fixture:string[]=["ch","ll","rr","qu","gu","bl","br","cl","cr","dr","fl","fr",
                                        "gl","gr","kl","kr","pl","pr","tl","tr"];
  tarjeta_fixture_png:any=null;
  fuente_fixture:string="Helvetica";
  fuente_fixture_negrita:string="Helvetica-Bold";

  constructor(private rolesPartidosService:RolesPartidosService) { }

  /* Los campeonatos a los que nunca se les abrio el modal de configuracion tienen
     configuracion_pdf_rol_partido en null, y la generacion del PDF se cortaba al leerla. Se
     completa con los mismos valores por defecto que propone ese modal. */
  asegurarConfiguracionPdfRolPartido(campeonato){
    if(!campeonato){
      return null;
    }
    let configuracion=campeonato.configuracion_pdf_rol_partido?campeonato.configuracion_pdf_rol_partido:{};
    let valor=function(dato,defecto){ return (dato==null)?defecto:dato; };
    campeonato.configuracion_pdf_rol_partido={
      distancia_partidos: valor(configuracion.distancia_partidos,30),
      mostrar_categoria: valor(configuracion.mostrar_categoria,false),
      mostrar_disciplina: valor(configuracion.mostrar_disciplina,false),
      mostrar_escenario: valor(configuracion.mostrar_escenario,false),
      campeonato_propio: valor(configuracion.campeonato_propio,true),
      caja_marcador: valor(configuracion.caja_marcador,true),
      usar_rol_version: valor(configuracion.usar_rol_version,1),
      logo_longitud: valor(configuracion.logo_longitud,25),
      logo_vertical: valor(configuracion.logo_vertical,0)
    };
    return campeonato.configuracion_pdf_rol_partido;
  }

  /* Trae el rol completo si llego sin partidos y deja solo los que se imprimen: los seleccionados,
     o si no hay ninguno seleccionado, todos los activos. */
  async prepararRolPartido(rol_partido_fuente){
    let rol_partido;
    if(!rol_partido_fuente.partidos || rol_partido_fuente.partidos.length==0){
      rol_partido=await this.rolesPartidosService.obtenerRolPartido(rol_partido_fuente.id).toPromise();
      //el campeonato vuelve del servidor sin normalizar, hay que completarle la configuracion otra vez
      this.asegurarConfiguracionPdfRolPartido(rol_partido.campeonato);
    }else{
      rol_partido=Object.assign({} ,rol_partido_fuente);
    }
    if(rol_partido.partidos.filter(p => p.activo && p.selected).length>0){
      rol_partido.partidos=rol_partido.partidos.filter(p => p.activo && p.selected);
    }else{
      rol_partido.partidos=rol_partido.partidos.filter(p => p.activo);
    }
    return rol_partido;
  }

  /* Dibuja el rol (ya preparado) y devuelve el documento sin cerrar, con su stream.
     opciones: usuario (obligatorio), version (1, 2 o 3 para las imagenes de redes, "LISTA" para el PDF
     del rol completo; si no, la del campeonato), numero_fondo (fija la cancha
     de fondo, si no sale al azar entre las 6) y cache_imagenes (reutiliza los logos entre llamadas). */
  async dibujarRolPartidos(rol_partido,doc_f,con_resultados,opciones){
    this.usuario=opciones.usuario;
    this.usar_cache_imagenes=!!opciones.cache_imagenes;
    this.asegurarConfiguracionPdfRolPartido(rol_partido.campeonato);
    let version=opciones.version?opciones.version:rol_partido.campeonato.configuracion_pdf_rol_partido.usar_rol_version;
    if(version=="LISTA"){
      return await this.dibujarRolPartidosLista(rol_partido,doc_f,con_resultados,opciones);
    }else if(version==1){
      return await this.dibujarRolPartidosV1(rol_partido,doc_f,con_resultados,opciones);
    }else if(version==2){
      return await this.dibujarRolPartidosV2(rol_partido,doc_f,con_resultados,opciones);
    }else{
      return await this.dibujarRolPartidosV3(rol_partido,doc_f,con_resultados,opciones);
    }
  }

  //dibuja, cierra el documento y entrega el PDF como Blob (lo usa la vista previa)
  async generarBlobRolPartidos(rol_partido,con_resultados,opciones):Promise<Blob>{
    let dibujo=await this.dibujarRolPartidos(rol_partido,null,con_resultados,opciones);
    return new Promise((resolver) => {
      dibujo.stream.on('finish',() => resolver(dibujo.stream.toBlob('application/pdf')));
      dibujo.doc.end();
    });
  }

  numeroFondo(opciones){
    return (opciones && opciones.numero_fondo)?opciones.numero_fondo:(Math.floor(Math.random() * 6) + 1);
  }

  imagen(url){
    if(!this.usar_cache_imagenes){
      return Util.convertirUrlToBase64Image(url);
    }
    if(!this.cache_imagenes.has(url)){
      //si falla no se guarda, asi el proximo intento la vuelve a pedir
      this.cache_imagenes.set(url,Util.convertirUrlToBase64Image(url).catch((error) => {
        this.cache_imagenes.delete(url);
        throw error;
      }));
    }
    return this.cache_imagenes.get(url);
  }

  limpiarCacheImagenes(){
    this.cache_imagenes.clear();
  }

  /* Lista clasica: el PDF del rol completo (boton "PDF de todo el rol"). Ya no es una de las
     versiones para redes. Hojas tamaño carta, con medidas fijas (35 pt entre partidos y logos de
     25 pt a la altura del nombre). Las barras de fecha y cancha son de un solo renglon y ocupan 22 pt,
     asi en una hoja entran 16 partidos con hasta dos barras. Una barra nunca queda sola al pie, y
     una cancha que sigue en la hoja siguiente repite su barra arriba. Correcciones respecto de la
     original:
     - la hora sale con dos digitos ("14:05", antes "14:5") y con su propia fuente, sin heredar la
       del partido anterior ni partirse en dos lineas;
     - los nombres de equipo se achican hasta entrar en una linea, sin pisar logos ni tarjetas;
     - la categoria va completa en una franja debajo de los nombres, sin cortarse;
     - el fondo se aclara de verdad (PDFKit ignora doc.opacity) y se repite hacia abajo en vez de
       estirarse o quedar cortado;
     - el color de categoria y disciplina solo se toma de nombre_corto cuando es un color, igual que
       en la version 2;
     - un logo que no carga, un partido sin escenario o sin datos de W.O. ya no cortan el PDF;
     - los partidos se agrupan por dia y, dentro del dia, por cancha: cada cancha lleva su barra con
       la fecha y el nombre de la cancha, y sus partidos van por hora. */
  distancia_partidos_lista:number=35;
  logo_lista:number=25;
  //alto real de un partido (la franja de categoria y la disciplina bajan hasta 34, antes de la fila
  //siguiente) y de la barra de fecha y cancha
  alto_partido_lista:number=34;
  alto_barra_lista:number=18;
  distancia_barra_lista:number=22;
  y_inicio_lista:number=92;
  margen_inferior_lista:number=15;

  async dibujarRolPartidosLista(rol_partido,doc_f,con_resultados,opciones){
    let distancia=this.distancia_partidos_lista;
    let inicioDia=(partido) => { let dia=new Date(partido.fecha_hora); dia.setHours(0,0,0,0); return dia.getTime(); };
    let nombreCancha=(partido) => (partido.escenario && partido.escenario.nombre)?String(partido.escenario.nombre):"";
    //dia, cancha (por nombre, con "Cancha 2" antes que "Cancha 10") y hora
    let partidos=rol_partido.partidos.slice().sort((a,b) =>
      (inicioDia(a)-inicioDia(b)) ||
      nombreCancha(a).localeCompare(nombreCancha(b),"es",{ numeric:true, sensitivity:"base" }) ||
      (new Date(a.fecha_hora).getTime()-new Date(b.fecha_hora).getTime()));
    //una barra de separacion cada vez que cambia el dia o la cancha
    let filas=[],clave_anterior=null;
    for(let i=0;i<partidos.length;i++){
      let clave=inicioDia(partidos[i])+"|"+nombreCancha(partidos[i]);
      if(clave!==clave_anterior){
        filas.push({ fecha_hora:partidos[i].fecha_hora, escenario:partidos[i].escenario });
        clave_anterior=clave;
      }
      filas.push(partidos[i]);
    }
    rol_partido.partidos=filas;
    let alto_pagina=GlobalVariable.Dictionary.PAPEL_CARTA_MEDIDA[1];
    let paginas=this.paginarFilasLista(rol_partido.partidos,alto_pagina-this.y_inicio_lista-this.margen_inferior_lista);
    let doc = doc_f?doc_f:new PDFDocument({size:[612,alto_pagina],margin:10});
    let stream = doc.pipe(blobStream());
    const numeroAleatorio = this.numeroFondo(opciones);
    //si la instalacion no tiene los fondos numerados se usa el general, en vez de cortar el PDF
    let fondo=await this.imagen(this.rest_server+"./img/fondo-rol-partido-"+numeroAleatorio+".png")
                        .catch(() => this.imagen(this.rest_server+"./img/fondo-rol-partido.png"));
    //aclarado al 50% como pedia el doc.opacity(0.5) original; se hornea al doble de resolucion
    fondo=await this.tenirImagenPdf(fondo,"#ffffff",0.5,612*2,alto_pagina*2);
    for(let p=0;p<paginas.length || p==0;p++){
      if(p>0){
        doc.addPage({size:[612,alto_pagina],margin:10});
      }
      await this.dibujarCabeceraRolPartido(doc,fondo,rol_partido,con_resultados,alto_pagina);
      let filas=paginas.length>0?paginas[p]:[];
      let y_partido=this.y_inicio_lista;
      for(let i=0;i<filas.length;i++){
        if(filas[i].equipo_1){
          await this.dibujarPartidoLista(doc,rol_partido,filas[i],y_partido,con_resultados);
          y_partido=y_partido+distancia;
        }else{
          this.dibujarSeparadorFechaLista(doc,rol_partido,filas[i],y_partido);
          y_partido=y_partido+this.distancia_barra_lista;
        }
      }
    }
    return { doc:doc, stream:stream };
  }

  /* Reparte las filas en hojas segun el alto que ocupa cada una (partido o barra). Una barra (fecha
     y cancha) nunca queda como ultima fila de una hoja: necesita lugar para al menos un partido
     debajo. Una cancha que no entra sigue en la hoja siguiente repitiendo su barra arriba. */
  paginarFilasLista(filas,alto_disponible){
    let paginas=[],actual=[],usado=0,ultimo_separador=null;
    for(let i=0;i<filas.length;i++){
      let es_separador=!filas[i].equipo_1;
      let necesita=es_separador?(this.distancia_barra_lista+this.alto_partido_lista):this.alto_partido_lista;
      if(actual.length>0 && usado+necesita>alto_disponible){
        paginas.push(actual);
        actual=[];
        usado=0;
        if(!es_separador && ultimo_separador){
          actual.push(ultimo_separador);
          usado=this.distancia_barra_lista;
        }
      }
      if(es_separador){
        ultimo_separador=filas[i];
      }
      actual.push(filas[i]);
      usado=usado+(es_separador?this.distancia_barra_lista:this.distancia_partidos_lista);
    }
    if(actual.length>0){
      paginas.push(actual);
    }
    return paginas;
  }

  imagenEquipoLista(equipo){
    return this.imagen(this.rest_server+equipo.logo)
               .catch(() => this.imagen(this.rest_server+"./img/icon-logo-equipo-default.png"));
  }

  async dibujarPartidoLista(doc,rol_partido,partido,y_partido,con_resultados){
    let configuracion=rol_partido.campeonato.configuracion_pdf_rol_partido;
    let colores=rol_partido.campeonato.configuracion_tarjeta_jugador;
    let base=colores.fondo_color_tarjeta;
    let logo=this.logo_lista;
    let y_logo=y_partido;
    let fecha_hora=new Date(partido.fecha_hora);

    //hora, y debajo la disciplina en su recuadro
    doc.fillColor(base);
    doc.font('Helvetica-Bold',13);
    if(configuracion.mostrar_disciplina && partido.disciplina){
      doc.text(this.horaTextoPdf(fecha_hora),11,y_partido+3,{width:60,align:"center",lineBreak:false});
      let color_disciplina=this.esColorPdf(partido.disciplina.nombre_corto)?partido.disciplina.nombre_corto:
                           (partido.disciplina.nombre=="BALONCESTO"?"#ed7213":base);
      doc.roundedRect(11,y_partido+17,60,17,5).fill(color_disciplina);
      doc.fillColor(colores.enunciados_color);
      let disciplina=partido.disciplina.nombre;
      this.tamanoBloqueFixture(doc,disciplina,'Helvetica',8,56,16,0,5);
      let lineas_disciplina=this.armarLineasFixture(doc,disciplina,56,0);
      let alto_linea_disciplina=doc.currentLineHeight(true);
      this.dibujarLineasFixture(doc,lineas_disciplina,13,y_partido+17+(17-lineas_disciplina.length*alto_linea_disciplina)/2+1,
                                56,'center',0,alto_linea_disciplina);
    }else{
      doc.text(this.horaTextoPdf(fecha_hora),11,y_partido+7,{width:60,align:"center",lineBreak:false});
    }

    //columnas: logo 1 | tarjetas 1 | nombre 1 | marcador | VS | marcador | nombre 2 | tarjetas 2 | logo 2
    let amarillas_1=(partido.lista_amarillas_equipo_1 && partido.lista_amarillas_equipo_1.length)?partido.lista_amarillas_equipo_1.length:0;
    let rojas_1=(partido.lista_rojas_equipo_1 && partido.lista_rojas_equipo_1.length)?partido.lista_rojas_equipo_1.length:0;
    let amarillas_2=(partido.lista_amarillas_equipo_2 && partido.lista_amarillas_equipo_2.length)?partido.lista_amarillas_equipo_2.length:0;
    let rojas_2=(partido.lista_rojas_equipo_2 && partido.lista_rojas_equipo_2.length)?partido.lista_rojas_equipo_2.length:0;
    let tarjetas_1=con_resultados && (amarillas_1>0 || rojas_1>0);
    let tarjetas_2=con_resultados && (amarillas_2>0 || rojas_2>0);
    let x_logo_1=74,x_logo_2=590-logo;
    let x_tarjetas_1=x_logo_1+logo+3,x_tarjetas_2=x_logo_2-16;
    let x_nombre_1=tarjetas_1?(x_tarjetas_1+16):(x_logo_1+logo+5);
    let ancho_nombre_1=289-x_nombre_1;
    let x_nombre_2=363;
    let ancho_nombre_2=(tarjetas_2?(x_tarjetas_2-3):(x_logo_2-5))-x_nombre_2;

    let logo_eq_1=await this.imagenEquipoLista(partido.equipo_1);
    let logo_eq_2=await this.imagenEquipoLista(partido.equipo_2);
    doc.image(logo_eq_1,x_logo_1,y_logo,{ width:logo, height:logo });
    doc.image(logo_eq_2,x_logo_2,y_logo,{ width:logo, height:logo });

    doc.fillColor("#000000");
    let tamano_1=this.tamanoLineaFixture(doc,partido.equipo_1.nombre,'Helvetica-Bold',16,ancho_nombre_1,0,8);
    doc.text(partido.equipo_1.nombre,x_nombre_1,y_partido+7+(16-tamano_1)/2,{width:ancho_nombre_1,align:"right",lineBreak:false,ellipsis:true});
    let tamano_2=this.tamanoLineaFixture(doc,partido.equipo_2.nombre,'Helvetica-Bold',16,ancho_nombre_2,0,8);
    doc.text(partido.equipo_2.nombre,x_nombre_2,y_partido+7+(16-tamano_2)/2,{width:ancho_nombre_2,align:"left",lineBreak:false,ellipsis:true});

    if(con_resultados){
      doc.font('Helvetica-Bold',16);
      doc.fillColor("#ff0000");
      if(partido.marcador_equipo_1!=null){
        doc.text(String(partido.marcador_equipo_1),289,y_partido+7,{width:22,align:"right",lineBreak:false});
      }
      if(partido.marcador_equipo_2!=null){
        doc.text(String(partido.marcador_equipo_2),341,y_partido+7,{width:22,align:"left",lineBreak:false});
      }
      this.dibujarTarjetasLista(doc,amarillas_1,rojas_1,x_tarjetas_1,y_partido);
      this.dibujarTarjetasLista(doc,amarillas_2,rojas_2,x_tarjetas_2,y_partido);
    }

    //VS en el color de la categoria; naranja si fue walk over
    let categoria=partido.categoria;
    let color_categoria=(categoria && this.esColorPdf(categoria.nombre_corto))?categoria.nombre_corto:base;
    let hubo_wo=(partido.w_o_equipo_1 && partido.w_o_equipo_1.es_wo) || (partido.w_o_equipo_2 && partido.w_o_equipo_2.es_wo);
    if(con_resultados && hubo_wo){
      color_categoria="#ed5f07";
    }
    doc.font('Helvetica-Bold',16);
    doc.fillColor(color_categoria);
    doc.text("VS",311,y_partido+7,{width:30,align:"center",lineBreak:false});
    /* La categoria va completa y en una sola linea, en una franja debajo de los nombres que se
       estira lo que haga falta (antes iba en un recuadro de 30 pt bajo el VS y se cortaba). Solo se
       achica si ni con todo el ancho disponible entre las tarjetas entra. */
    if(configuracion.mostrar_categoria && categoria){
      let texto_categoria=String(categoria.nombre).toUpperCase();
      let tamano_categoria=this.tamanoLineaFixture(doc,texto_categoria,'Helvetica-Bold',9,360,0.4,6);
      let ancho_categoria=doc.widthOfString(texto_categoria,{ characterSpacing:0.4 })+16;
      let alto_categoria=11;
      let x_categoria=326-ancho_categoria/2;
      doc.roundedRect(x_categoria,y_partido+23,ancho_categoria,alto_categoria,alto_categoria/2).fill(color_categoria);
      doc.fillColor(colores.enunciados_color);
      doc.text(texto_categoria,x_categoria,y_partido+23+(alto_categoria-tamano_categoria)/2+0.5,
               { width:ancho_categoria, align:"center", characterSpacing:0.4, lineBreak:false });
    }
    doc.font('Helvetica-Bold',16);
  }

  //amarilla arriba y roja debajo, cada una con su cantidad
  dibujarTarjetasLista(doc,amarillas,rojas,x,y){
    doc.font('Helvetica-Bold',10);
    if(amarillas>0){
      doc.rect(x,y,13,13).fill("#ffff00");
      doc.fillColor("#000000");
      doc.text(String(amarillas),x,y+2,{width:13,align:"center",lineBreak:false});
    }
    if(rojas>0){
      doc.rect(x,y+15,13,13).fill("#ff0000");
      doc.fillColor("#ffffff");
      doc.text(String(rojas),x,y+17,{width:13,align:"center",lineBreak:false});
    }
  }

  /* Barra de un solo renglon: dia y fecha a la izquierda, cancha a la derecha. Empieza donde empieza
     el recuadro de la disciplina (x 11) y termina donde termina el logo del equipo 2 (x 590), asi
     queda alineada con la columna de los partidos. La cancha se achica (o se corta con puntos
     suspensivos) antes que la fecha. */
  dibujarSeparadorFechaLista(doc,rol_partido,fila,y_partido){
    let colores=rol_partido.campeonato.configuracion_tarjeta_jugador;
    let fecha_hora=new Date(fila.fecha_hora);
    let alto=this.alto_barra_lista,x=11,ancho=590-11,margen=8;
    doc.roundedRect(x,y_partido,ancho,alto,4).fill(colores.fondo_color_tarjeta);
    doc.fillColor(colores.enunciados_color);
    let texto_fecha=fecha_hora.toLocaleDateString("es-ES",{ weekday:'long' }).toUpperCase()+" "+fecha_hora.getDate()+
                    " DE "+fecha_hora.toLocaleString("es-ES",{ month:'long' }).toUpperCase()+" "+fecha_hora.getFullYear();
    let tamano_fecha=this.tamanoLineaFixture(doc,texto_fecha,'Helvetica-Bold',11,ancho*0.6,0.3,7);
    let ancho_fecha=doc.widthOfString(texto_fecha,{ characterSpacing:0.3 });
    doc.text(texto_fecha,x+margen,y_partido+(alto-tamano_fecha)/2+1,{ characterSpacing:0.3, lineBreak:false });
    //el rol va dividido por cancha, asi que la barra siempre dice de que cancha son los partidos
    if(fila.escenario && fila.escenario.nombre){
      let cancha=String(fila.escenario.nombre).toUpperCase();
      let ancho_cancha=ancho-2*margen-ancho_fecha-16;
      let tamano_cancha=this.tamanoLineaFixture(doc,cancha,'Helvetica-Bold',10,ancho_cancha,0.3,7);
      doc.text(cancha,x+ancho-margen-ancho_cancha,y_partido+(alto-tamano_cancha)/2+1,
               { width:ancho_cancha, align:"right", characterSpacing:0.3, lineBreak:false, ellipsis:true });
    }
    doc.font('Helvetica-Bold',16);
  }

  /* ─── Version 1 para redes: tarjetas de partido en cuadricula ──────────────────────────────
     Pensada para la previa de 2 a 8 partidos (se publica 24 horas antes) o para sus resultados.
     Imagen 4:5 de 612x765 pt (1080x1350 px una vez convertida), la proporcion que Facebook e
     Instagram muestran entera en el feed. Igual que las otras versiones, la fecha y la cancha van
     arriba, en la barra de la version 2, y debajo las tarjetas de esa fecha y cancha. Con 3
     partidos o menos en la imagen las tarjetas van en una columna; con mas, en dos (si un grupo es
     impar, su ultima tarjeta va centrada). Con mas de 8 partidos sale una imagen por cada 8.
     Usa el fondo tenido, las fuentes incrustadas, las tarjetas translucidas y la cabecera de la
     version 2. */
  partidos_por_imagen_redes:number=8;

  async dibujarRolPartidosV1(rol_partido,doc_f,con_resultados,opciones){
    let partidos=rol_partido.partidos.filter(partido => partido.equipo_1 && partido.equipo_2)
                                     .sort((a,b) => new Date(a.fecha_hora).getTime()-new Date(b.fecha_hora).getTime());
    let paginas=[];
    for(let i=0;i<partidos.length;i=i+this.partidos_por_imagen_redes){
      paginas.push(partidos.slice(i,i+this.partidos_por_imagen_redes));
    }
    if(paginas.length==0){
      paginas.push([]);
    }
    let doc = doc_f?doc_f:new PDFDocument({size:[this.ancho_fixture,this.alto_fixture],margin:10});
    let stream = doc.pipe(blobStream());
    let base=rol_partido.campeonato.configuracion_tarjeta_jugador.fondo_color_tarjeta;
    const numeroAleatorio = this.numeroFondo(opciones);
    let fondo=await this.imagen(this.rest_server+"./img/fondo-rol-partido-futsal-"+numeroAleatorio+".png");
    //se tiñe una sola vez, al doble de resolucion, y se reutiliza en todas las imagenes
    fondo=await this.tenirImagenPdf(fondo,this.oscurecerColorPdf(base,0.55),this.intensidad_tinte_fixture,
                                    this.ancho_fixture*2,this.alto_fixture*2);
    await this.prepararFuentesFixture(doc);
    //las tarjetas translucidas son PNG: se arma una por cada tamaño y se reutiliza
    let tarjetas_png={};
    let por_cancha=rol_partido.campeonato.configuracion_pdf_rol_partido.mostrar_escenario;
    for(let p=0;p<paginas.length;p++){
      if(p>0){
        doc.addPage({size:[this.ancho_fixture,this.alto_fixture],margin:10});
      }
      await this.dibujarCabeceraRolPartidoV2(doc,fondo,rol_partido,con_resultados);

      //grupos de la imagen: un dia (y una cancha, si se muestra el escenario) por grupo
      let grupos=[];
      paginas[p].forEach(partido => {
        let clave=new Date(partido.fecha_hora).toDateString()+"|"+((por_cancha && partido.escenario)?partido.escenario.nombre:"");
        let grupo=grupos.find(existente => existente.clave==clave);
        if(!grupo){
          grupo={ clave:clave, partidos:[] };
          grupos.push(grupo);
        }
        grupo.partidos.push(partido);
      });
      /* La barra de fecha sale del mismo tamaño que en la version 2 con esas mismas filas (partidos
         mas barras), asi el titulo del dia se lee igual en todos los fixtures. */
      let escala_barra=this.planificarFixture(paginas[p].length+grupos.length).escala;
      grupos.forEach(grupo => {
        grupo.alto_barra=((por_cancha && grupo.partidos[0].escenario)?50:34)*escala_barra;
      });

      //el alto de las tarjetas sale de lo que queda despues de las barras
      let cantidad=paginas[p].length;
      let columnas=cantidad<=3?1:2;
      //el contenido arranca pegado a la cabecera (que termina en 99), no centrado en el alto
      let x_inicio=20,y_cuerpo=107,ancho_util=572,alto_util=this.alto_fixture-18-y_cuerpo;
      let separacion=12,separacion_barra=8;
      let filas_totales=grupos.reduce((suma,grupo) => suma+Math.ceil(grupo.partidos.length/columnas),0);
      let alto_barras=grupos.reduce((suma,grupo) => suma+grupo.alto_barra+separacion_barra,0);
      let espacios=filas_totales-grupos.length+(grupos.length-1);
      let ancho=(ancho_util-(columnas-1)*separacion)/columnas;
      /* En una columna (3 partidos o menos) la tarjeta crece hasta llenar el alto disponible, y con
         ella todo su contenido; con un tope proporcional al ancho para que no quede alargada. Antes
         el tope fijo de 190 dejaba 2 partidos chicos y media imagen vacia. */
      let alto=Math.max(60,Math.min((alto_util-alto_barras-espacios*separacion)/Math.max(1,filas_totales),columnas==1?ancho*0.6:230));
      let y=y_cuerpo;

      for(let g=0;g<grupos.length;g++){
        let grupo=grupos[g];
        this.dibujarSeparadorFechaV2(doc,rol_partido,grupo.partidos[0],y,{ escala:escala_barra, alto_tarjeta:grupo.alto_barra });
        y=y+grupo.alto_barra+separacion_barra;
        let en_grupo=grupo.partidos.length;
        for(let i=0;i<en_grupo;i++){
          let x=x_inicio+(i%columnas)*(ancho+separacion);
          if(columnas==2 && en_grupo%2==1 && i==en_grupo-1){
            x=x_inicio+(ancho_util-ancho)/2;
          }
          let y_tarjeta=y+Math.floor(i/columnas)*(alto+separacion);
          await this.dibujarTarjetaPartidoV1(doc,rol_partido,grupo.partidos[i],x,y_tarjeta,ancho,alto,con_resultados,tarjetas_png);
        }
        let filas_grupo=Math.ceil(en_grupo/columnas);
        y=y+filas_grupo*alto+(filas_grupo-1)*separacion+separacion;
      }
    }
    return { doc:doc, stream:stream };
  }

  /* Tarjeta de un partido, en diagonal: el equipo 1 arriba a la izquierda (logo en la esquina y el
     nombre a su derecha) y el equipo 2 abajo a la derecha (logo en la esquina y el nombre a su
     izquierda). En el centro, centradas, la hora y debajo las etiquetas de categoria y disciplina.
     Con resultados, el marcador de cada equipo va en la esquina libre de su fila (arriba a la
     derecha el del 1, abajo a la izquierda el del 2), con sus tarjetas al lado. La fecha y la
     cancha van en la barra del grupo. Todo escala con el tamaño de la tarjeta. */
  async dibujarTarjetaPartidoV1(doc,rol_partido,partido,x,y,ancho,alto,con_resultados,tarjetas_png){
    let colores=rol_partido.campeonato.configuracion_tarjeta_jugador;
    let configuracion=rol_partido.campeonato.configuracion_pdf_rol_partido;
    let base=colores.fondo_color_tarjeta;
    let escala=Math.min(alto/150,ancho/280);
    let radio=10*escala;
    let fecha_hora=new Date(partido.fecha_hora);

    let color_categoria=(partido.categoria && this.esColorPdf(partido.categoria.nombre_corto))?partido.categoria.nombre_corto:base;
    let hubo_wo=(partido.w_o_equipo_1 && partido.w_o_equipo_1.es_wo) || (partido.w_o_equipo_2 && partido.w_o_equipo_2.es_wo);
    if(con_resultados && hubo_wo){
      color_categoria="#ed5f07";
    }

    let clave=Math.round(ancho)+"x"+Math.round(alto);
    if(!tarjetas_png[clave]){
      tarjetas_png[clave]=this.tarjetaFixturePng(ancho,alto,
        this.oscurecerColorPdf(base,this.oscurecimientoTarjetaFixture(base)),this.opacidad_tarjeta_fixture,radio);
    }
    doc.image(tarjetas_png[clave],x,y,{ width:ancho, height:alto });
    doc.lineWidth(1);
    doc.roundedRect(x,y,ancho,alto,radio).stroke(this.aclararColorPdf(base,0.25));
    //franja de la categoria arriba, recortada al radio de la tarjeta
    let alto_franja=5*escala;
    doc.save();
    doc.roundedRect(x,y,ancho,alto,radio).clip();
    doc.rect(x,y,ancho,alto_franja).fill(color_categoria);
    doc.restore();

    //etiquetas de categoria y disciplina (van en el centro, debajo de la hora)
    let etiquetas=[];
    if(configuracion.mostrar_categoria && partido.categoria){
      etiquetas.push({ texto:String(partido.categoria.nombre).toUpperCase(), color:color_categoria });
    }
    if(configuracion.mostrar_disciplina && partido.disciplina){
      etiquetas.push({ texto:String(partido.disciplina.nombre).toUpperCase(),
                       color:this.esColorPdf(partido.disciplina.nombre_corto)?partido.disciplina.nombre_corto:this.oscurecerColorPdf(base,0.2) });
    }

    //reparto vertical: fila del equipo 1, bloque central, fila del equipo 2
    let margen=9*escala,separacion=5*escala;
    let alto_pastilla=22*escala,alto_etiqueta=14*escala;
    //el VS va arriba de la hora, entre los dos equipos
    let tamano_vs=18*escala,alto_vs=tamano_vs+3*escala;
    let alto_centro=alto_vs+alto_pastilla+(etiquetas.length>0?(4*escala+alto_etiqueta):0);
    //el tope del logo acompaña a la escala, para que en las tarjetas grandes no quede chico
    let logo=Math.max(16,Math.min((alto-alto_franja-2*margen-alto_centro-2*separacion)/2,ancho*0.28,Math.max(80,60*escala)));
    let y_fila_1=y+alto_franja+margen;
    let y_fila_2=y+alto-margen-logo;
    let y_centro=y_fila_1+logo+((y_fila_2-(y_fila_1+logo))-alto_centro)/2;
    let x_centro=x+ancho/2;

    let logo_eq_1=await this.imagenEquipoLista(partido.equipo_1);
    let logo_eq_2=await this.imagenEquipoLista(partido.equipo_2);
    let x_logo_1=x+margen,x_logo_2=x+ancho-margen-logo;
    doc.image(logo_eq_1,x_logo_1,y_fila_1,{ width:logo, height:logo });
    doc.image(logo_eq_2,x_logo_2,y_fila_2,{ width:logo, height:logo });

    //con resultados, la esquina libre de cada fila lleva el marcador y las tarjetas de ese equipo
    let ancho_marcador=con_resultados?34*escala:0;
    let amarillas_1=(partido.lista_amarillas_equipo_1 && partido.lista_amarillas_equipo_1.length)?1:0;
    let rojas_1=(partido.lista_rojas_equipo_1 && partido.lista_rojas_equipo_1.length)?1:0;
    let amarillas_2=(partido.lista_amarillas_equipo_2 && partido.lista_amarillas_equipo_2.length)?1:0;
    let rojas_2=(partido.lista_rojas_equipo_2 && partido.lista_rojas_equipo_2.length)?1:0;
    let ancho_tarjetas_1=con_resultados?(amarillas_1+rojas_1)*13*escala:0;
    let ancho_tarjetas_2=con_resultados?(amarillas_2+rojas_2)*13*escala:0;

    //nombres al costado de su logo, hasta dos renglones del alto del logo; los dos del mismo tamaño
    let espacio=8*escala;
    let ancho_nombre_1=ancho-2*margen-logo-espacio-(con_resultados?(ancho_marcador+ancho_tarjetas_1+espacio):0);
    let ancho_nombre_2=ancho-2*margen-logo-espacio-(con_resultados?(ancho_marcador+ancho_tarjetas_2+espacio):0);
    let nombre_1=String(partido.equipo_1.nombre).toUpperCase(),nombre_2=String(partido.equipo_2.nombre).toUpperCase();
    let tamano_nombre=Math.min(
      this.tamanoBloqueFixture(doc,nombre_1,this.fuente_fixture_negrita,13*escala,ancho_nombre_1,logo,0.2,6),
      this.tamanoBloqueFixture(doc,nombre_2,this.fuente_fixture_negrita,13*escala,ancho_nombre_2,logo,0.2,6));
    doc.font(this.fuente_fixture_negrita,tamano_nombre);
    doc.fillColor("#ffffff");
    let alto_linea=doc.currentLineHeight(true);
    let lineas_1=this.armarLineasFixture(doc,nombre_1,ancho_nombre_1,0.2);
    let lineas_2=this.armarLineasFixture(doc,nombre_2,ancho_nombre_2,0.2);
    this.dibujarLineasFixture(doc,lineas_1,x_logo_1+logo+espacio,y_fila_1+(logo-lineas_1.length*alto_linea)/2+1,
                              ancho_nombre_1,'left',0.2,alto_linea);
    this.dibujarLineasFixture(doc,lineas_2,x_logo_2-espacio-ancho_nombre_2,y_fila_2+(logo-lineas_2.length*alto_linea)/2+1,
                              ancho_nombre_2,'right',0.2,alto_linea);

    if(con_resultados){
      let color_marcador=colores.datos_color?colores.datos_color:this.aclararColorPdf(base,0.4);
      let tamano_marcador=Math.min(26*escala,logo*0.9);
      doc.font(this.fuente_fixture_negrita,tamano_marcador);
      doc.fillColor(color_marcador);
      if(partido.marcador_equipo_1!=null){
        doc.text(String(partido.marcador_equipo_1),x+ancho-margen-ancho_marcador,y_fila_1+(logo-tamano_marcador)/2+1,
                 { width:ancho_marcador, align:'right', lineBreak:false });
      }
      if(partido.marcador_equipo_2!=null){
        doc.text(String(partido.marcador_equipo_2),x+margen,y_fila_2+(logo-tamano_marcador)/2+1,
                 { width:ancho_marcador, align:'left', lineBreak:false });
      }
      let plan_tarjetas={ escala:escala*0.85 };
      this.dibujarTarjetasV2(doc,partido.lista_amarillas_equipo_1,partido.lista_rojas_equipo_1,
                             x+ancho-margen-ancho_marcador-3*escala,y_fila_1+(logo-15*plan_tarjetas.escala)/2,'derecha',plan_tarjetas);
      this.dibujarTarjetasV2(doc,partido.lista_amarillas_equipo_2,partido.lista_rojas_equipo_2,
                             x+margen+ancho_marcador+3*escala,y_fila_2+(logo-15*plan_tarjetas.escala)/2,'izquierda',plan_tarjetas);
    }

    //centro: el VS, la hora en su pastilla y debajo categoria y disciplina, todo centrado
    doc.font(this.fuente_fixture_negrita,tamano_vs);
    doc.fillColor("#ffffff");
    doc.text("VS",x_centro-40*escala,y_centro,{ width:80*escala, align:'center', characterSpacing:1, lineBreak:false });
    y_centro=y_centro+alto_vs;
    let ancho_pastilla=Math.min(74*escala,ancho*0.3);
    doc.roundedRect(x_centro-ancho_pastilla/2,y_centro,ancho_pastilla,alto_pastilla,alto_pastilla/2).fill(base);
    doc.fillColor(colores.enunciados_color);
    let hora=this.horaTextoPdf(fecha_hora);
    let tamano_hora=this.tamanoLineaFixture(doc,hora,this.fuente_fixture_negrita,15*escala,ancho_pastilla-8*escala,0.6,8);
    doc.text(hora,x_centro-ancho_pastilla/2,y_centro+(alto_pastilla-tamano_hora)/2+1,
             { width:ancho_pastilla, align:'center', characterSpacing:0.6, lineBreak:false });
    if(etiquetas.length>0){
      let y_etiquetas=y_centro+alto_pastilla+4*escala;
      let ancho_maximo=(ancho-2*margen)/etiquetas.length-6*escala;
      let medidas=etiquetas.map(etiqueta => {
        let tamano=this.tamanoLineaFixture(doc,etiqueta.texto,this.fuente_fixture_negrita,7.5*escala,ancho_maximo-12*escala,0.6,5);
        return { tamano:tamano, ancho:doc.widthOfString(etiqueta.texto,{ characterSpacing:0.6 })+12*escala };
      });
      let total=medidas.reduce((suma,medida) => suma+medida.ancho,0)+(etiquetas.length-1)*6*escala;
      let x_etiqueta=x_centro-total/2;
      for(let i=0;i<etiquetas.length;i++){
        doc.roundedRect(x_etiqueta,y_etiquetas,medidas[i].ancho,alto_etiqueta,alto_etiqueta/2).fill(etiquetas[i].color);
        doc.fillColor("#ffffff");
        doc.font(this.fuente_fixture_negrita,medidas[i].tamano);
        doc.text(etiquetas[i].texto,x_etiqueta,y_etiquetas+(alto_etiqueta-medidas[i].tamano)/2+0.5,
                 { width:medidas[i].ancho, align:'center', characterSpacing:0.6, lineBreak:false });
        x_etiqueta=x_etiqueta+medidas[i].ancho+6*escala;
      }
    }
  }

  async dibujarRolPartidosV2(rol_partido,doc_f,con_resultados,opciones){
    let fechas = rol_partido.partidos.filter((partido, i, arr) => arr.findIndex(t => new Date(t.fecha_hora).toDateString() === new Date(partido.fecha_hora).toDateString()) === i);
    for(let i=0;i<fechas.length;i++){
      let indice=rol_partido.partidos.indexOf(fechas[i]);
      rol_partido.partidos.splice(indice,0,{fecha_hora:fechas[i].fecha_hora,escenario:fechas[i].escenario})
    }
    /* El plan acomoda todas las filas en una imagen y de ahi sale la escala de todo el dibujo. La
       distancia_partidos configurada ya no interviene: en este fixture la marca el lienzo. */
    let plan=this.planificarFixture(rol_partido.partidos.length);
    let doc = doc_f?doc_f:new PDFDocument({size:[this.ancho_fixture,plan.alto_pagina],margin:10});
    let stream = doc.pipe(blobStream());
    const numeroAleatorio = this.numeroFondo(opciones);
    let fondo=await this.imagen(this.rest_server+"./img/fondo-rol-partido-futsal-"+numeroAleatorio+".png");
    //cada documento arma su propia tarjeta, porque depende del color del campeonato
    this.tarjeta_fixture_png=null;
    //se tiñe una sola vez, ya del tamaño de la pagina, y se reutiliza en todas
    fondo=await this.tenirImagenPdf(fondo,
            this.oscurecerColorPdf(rol_partido.campeonato.configuracion_tarjeta_jugador.fondo_color_tarjeta,0.55),
            this.intensidad_tinte_fixture,this.ancho_fixture,plan.alto_pagina);
    await this.prepararFuentesFixture(doc);
    await this.dibujarCabeceraRolPartidoV2(doc,fondo,rol_partido,con_resultados);
    let y_partido=plan.y_inicio;
    for(let i=0;i<rol_partido.partidos.length;i++){
      if(rol_partido.partidos[i].equipo_1){
        await this.dibujarPartidoV2(doc,rol_partido,rol_partido.partidos[i],y_partido,con_resultados,plan);
      }else{
        this.dibujarSeparadorFechaV2(doc,rol_partido,rol_partido.partidos[i],y_partido,plan);
      }
      y_partido=y_partido+plan.paso;
    }
    return { doc:doc, stream:stream };
  }

  /* Acomoda todas las filas (partidos y separadores de fecha) en una sola imagen y saca de ahi la
     escala del dibujo: la tarjeta ocupa el 82% del paso y todas las medidas cuelgan de su alto.
     Mientras el rol entre en el 4:5 las filas se estiran para llenarlo, asi con pocos partidos las
     letras salen mas grandes. Pasado ese punto el paso ya no se aprieta mas: la imagen crece hacia
     abajo y las letras se mantienen.
     Lo horizontal crece amortiguado, si no los logos y el bloque central se comen el ancho que
     necesitan los nombres de los equipos. */
  planificarFixture(cantidad_filas){
    let filas=Math.max(1,cantidad_filas);
    let alto_util=this.alto_fixture-this.y_inicio_fixture-this.margen_inferior_fixture;
    let paso=Math.max(alto_util/filas,this.paso_minimo_fixture);
    let alto_tarjeta=Math.min(paso*0.82,this.paso_maximo_fixture*0.82);
    let escala=alto_tarjeta/74;
    return {
      filas:filas,
      paso:paso,
      alto_tarjeta:alto_tarjeta,
      escala:escala,
      escala_horizontal:1+(escala-1)*0.45,
      y_inicio:this.y_inicio_fixture+(paso-alto_tarjeta)/2,
      alto_pagina:Math.max(this.alto_fixture,this.y_inicio_fixture+filas*paso+this.margen_inferior_fixture)
    };
  }

  /* Baja el tamaño de a poco hasta que el texto entra en una sola linea. Deja la fuente ya
     aplicada en el documento, lista para el doc.text. */
  tamanoLineaFixture(doc,texto,fuente,tamano,ancho,espaciado,minimo){
    doc.font(fuente,tamano);
    while(tamano>minimo && doc.widthOfString(texto,{ characterSpacing:espaciado })>ancho){
      tamano=tamano-0.5;
      doc.font(fuente,tamano);
    }
    return tamano;
  }

  /* Igual, pero admitiendo varias lineas: baja hasta que el texto, ya repartido en lineas y con los
     cortes silabicos, entra a lo ancho y a lo alto. Asi "DEPORTIVO SAN JOSÉ" se parte en dos lineas
     grandes en vez de encogerse, y solo se achica cuando ni partido entra. */
  tamanoBloqueFixture(doc,texto,fuente,tamano,ancho,alto,espaciado,minimo){
    let me=this;
    let noEntra=function(){
      let lineas=me.armarLineasFixture(doc,texto,ancho,espaciado);
      for(let i=0;i<lineas.length;i++){
        if(doc.widthOfString(lineas[i],{ characterSpacing:espaciado })>ancho){
          return true;
        }
      }
      return lineas.length*doc.currentLineHeight(true)>alto;
    };
    doc.font(fuente,tamano);
    while(tamano>minimo && noEntra()){
      tamano=tamano-0.5;
      doc.font(fuente,tamano);
    }
    return tamano;
  }

  /* ─── Corte de palabras del fixture ────────────────────────────────────────────
     PDFKit parte las palabras que no entran por donde caiga ("ACADEMI/A"), asi que el reparto en
     lineas se hace a mano: se cortan por silabas, con guion, respetando que los digrafos ch, ll y
     rr no se separan y que no puede quedar una vocal sola al final de una linea ni al principio de
     la siguiente. */

  formanDiptongoFixture(a,b){
    //una debil acentuada (í, ú) siempre rompe el diptongo, igual que dos vocales fuertes juntas
    if(this.vocales_debiles_acentuadas_fixture.indexOf(a)>=0 || this.vocales_debiles_acentuadas_fixture.indexOf(b)>=0){
      return false;
    }
    if(this.vocales_fuertes_fixture.indexOf(a)>=0 && this.vocales_fuertes_fixture.indexOf(b)>=0){
      return false;
    }
    return a!=b;
  }

  silabasFixture(palabra){
    //las siglas y todo lo que lleve puntos o numeros no se cortan
    if(!/^[a-záéíóúüñ]+$/i.test(palabra)){
      return [palabra];
    }
    let me=this;
    let minusculas=palabra.toLowerCase();
    let esVocal=function(i){
      let c=minusculas[i];
      if(me.vocales_fuertes_fixture.indexOf(c)<0 && me.vocales_debiles_fixture.indexOf(c)<0 &&
         me.vocales_debiles_acentuadas_fixture.indexOf(c)<0){
        return false;
      }
      //la u de que/qui y gue/gui no suena: cuenta como consonante
      if(c=="u" && i>0 && (minusculas[i-1]=="q" || minusculas[i-1]=="g") && i+1<minusculas.length &&
         "eéií".indexOf(minusculas[i+1])>=0){
        return false;
      }
      return true;
    };
    let silabas=[],inicio=0,i=0;
    while(i<minusculas.length){
      while(i<minusculas.length && !esVocal(i)){
        i++;
      }
      if(i>=minusculas.length){
        break;
      }
      //el nucleo se estira mientras las vocales sigan formando diptongo o triptongo
      let nucleo=i;
      while(nucleo+1<minusculas.length && esVocal(nucleo+1) &&
            this.formanDiptongoFixture(minusculas[nucleo],minusculas[nucleo+1])){
        nucleo++;
      }
      let j=nucleo+1,consonantes=[];
      while(j<minusculas.length && !esVocal(j)){
        consonantes.push(j);
        j++;
      }
      if(j>=minusculas.length){
        break;
      }
      //reparto de las consonantes que quedan entre este nucleo y el siguiente
      let cantidad=consonantes.length,corte;
      if(cantidad==0){
        corte=nucleo+1;
      }else if(cantidad==1){
        corte=consonantes[0];
      }else if(cantidad==2){
        corte=this.grupos_inseparables_fixture.indexOf(minusculas.substr(consonantes[0],2))>=0?consonantes[0]:consonantes[1];
      }else if(cantidad==3){
        corte=this.grupos_inseparables_fixture.indexOf(minusculas.substr(consonantes[1],2))>=0?consonantes[1]:consonantes[2];
      }else{
        corte=consonantes[cantidad-2];
      }
      silabas.push(palabra.substring(inicio,corte));
      inicio=corte;
      i=j;
    }
    silabas.push(palabra.substring(inicio));
    return silabas.filter(silaba => silaba.length>0);
  }

  /* Corta la palabra en el ultimo punto silabico que todavia entra en el ancho. Devuelve la parte
     izquierda ya con el guion, o null si no hay corte permitido. */
  cortarPalabraFixture(doc,palabra,ancho,espaciado){
    let silabas=this.silabasFixture(palabra);
    if(silabas.length<2){
      return null;
    }
    //una silaba de una sola letra es siempre una vocal: no puede quedar sola en ninguna de las dos lineas
    let primero=(silabas[0].length==1)?2:1;
    let ultimo=silabas.length-((silabas[silabas.length-1].length==1)?2:1);
    let mejor=null;
    for(let i=primero;i<=ultimo;i++){
      let izquierda=silabas.slice(0,i).join("")+"-";
      if(doc.widthOfString(izquierda,{ characterSpacing:espaciado })>ancho){
        break;
      }
      mejor={ izquierda:izquierda, derecha:silabas.slice(i).join("") };
    }
    return mejor;
  }

  armarLineasFixture(doc,texto,ancho,espaciado){
    let mide=texto_medido => doc.widthOfString(texto_medido,{ characterSpacing:espaciado });
    let lineas=[],actual="";
    let palabras=texto.split(/\s+/).filter(palabra => palabra.length>0);
    for(let p=0;p<palabras.length;p++){
      let candidata=actual?(actual+" "+palabras[p]):palabras[p];
      if(mide(candidata)<=ancho){
        actual=candidata;
        continue;
      }
      if(actual){
        lineas.push(actual);
        actual="";
      }
      let resto=palabras[p];
      while(mide(resto)>ancho){
        let corte=this.cortarPalabraFixture(doc,resto,ancho,espaciado);
        if(!corte){
          //sin corte posible se deja entera: la achica el tamanoBloqueFixture
          break;
        }
        lineas.push(corte.izquierda);
        resto=corte.derecha;
      }
      actual=resto;
    }
    if(actual){
      lineas.push(actual);
    }
    return lineas;
  }

  dibujarLineasFixture(doc,lineas,x,y,ancho,alineacion,espaciado,alto_linea){
    for(let i=0;i<lineas.length;i++){
      doc.text(lineas[i],x,y+i*alto_linea,
               { width:ancho, align:alineacion, characterSpacing:espaciado, lineBreak:false });
    }
  }

  async dibujarRolPartidosV3(rol_partido,doc_f,con_resultados,opciones){
    rol_partido.campeonato.configuracion_pdf_rol_partido.distancia_partidos=60;
    let fechas = rol_partido.partidos.filter((partido, i, arr) => arr.findIndex(t => new Date(t.fecha_hora).toDateString() === new Date(partido.fecha_hora).toDateString()) === i);
    for(let i=0;i<fechas.length;i++){
      let indice=rol_partido.partidos.indexOf(fechas[i]);
      rol_partido.partidos.splice(indice,0,{fecha_hora:fechas[i].fecha_hora,escenario:fechas[i].escenario})
    }
    let doc = doc_f?doc_f:new PDFDocument({size:[612, Math.max(150+(rol_partido.campeonato.configuracion_pdf_rol_partido.distancia_partidos*rol_partido.partidos.length),468)],margin:10});
    let stream = doc.pipe(blobStream());
    const numeroAleatorio = this.numeroFondo(opciones);
    let fondo=await this.imagen(this.rest_server+"./img/fondo-rol-partido-futsal-"+numeroAleatorio+".png");
    await this.dibujarCabeceraRolPartido(doc,fondo,rol_partido,con_resultados);
    let y_partido=80,items=0,page=1,partidos_por_pagina=20;
    for(let i=0;i<rol_partido.partidos.length;i++){
      doc.switchToPage(page-1);
      items++;
      if(rol_partido.partidos[i].equipo_1){

        doc.roundedRect(20,y_partido+25,256,30,10).fill(rol_partido.campeonato.configuracion_tarjeta_jugador.fondo_color_tarjeta).stroke();
        doc.roundedRect(336,y_partido+25,256,30,10).fill(rol_partido.campeonato.configuracion_tarjeta_jugador.fondo_color_tarjeta).stroke();
        //doc.roundedRect(20,y_partido-20,572,50,5).fill("#dae5e5").fillOpacity(0.8).stroke();
        
        doc.font('Helvetica-Bold',16);
        doc.fillColor(rol_partido.campeonato.configuracion_tarjeta_jugador.enunciados_color);
        doc.text(rol_partido.partidos[i].equipo_1.nombre,20,y_partido+35,{width:266,align:"center"});
        let logo_eq_1=await this.imagen(this.rest_server+rol_partido.partidos[i].equipo_1.logo);
        doc.image(logo_eq_1,10,y_partido+5, { width: 50, height: 50,align:'left' });
        doc.fillColor(rol_partido.campeonato.configuracion_tarjeta_jugador.fondo_color_tarjeta);

        let color_categoria;
        if(!con_resultados){
          color_categoria=rol_partido.partidos[i].categoria.nombre_corto?rol_partido.partidos[i].categoria.nombre_corto:rol_partido.campeonato.configuracion_tarjeta_jugador.fondo_color_tarjeta;
        }else{
          if(rol_partido.partidos[i].w_o_equipo_1.es_wo || rol_partido.partidos[i].w_o_equipo_2.es_wo){
            color_categoria="#ed5f07";
          }else{
            color_categoria=rol_partido.partidos[i].categoria.nombre_corto?rol_partido.partidos[i].categoria.nombre_corto:rol_partido.campeonato.configuracion_tarjeta_jugador.fondo_color_tarjeta;
          }
        }

        doc.roundedRect(276,y_partido+10,60,48,5).fill(color_categoria).stroke();
        if(rol_partido.campeonato.configuracion_pdf_rol_partido.mostrar_categoria){
          doc.fillColor(rol_partido.campeonato.configuracion_tarjeta_jugador.datos_color);
          doc.font('Helvetica-Bold',10);
          doc.text(rol_partido.partidos[i].categoria.nombre,0,y_partido+47,{width:612,align:"center"});
          doc.font('Helvetica-Bold',16);
        }
        /* La disciplina se dibuja despues del recuadro de la categoria y debajo de el: antes iba
           primero y en el mismo lugar, y el recuadro la tapaba (a baloncesto casi entera). Cabe
           entre el recuadro y la tarjeta siguiente, que empieza 60 puntos mas abajo. */
        if(rol_partido.campeonato.configuracion_pdf_rol_partido.mostrar_disciplina){
          let disciplina=rol_partido.partidos[i].disciplina;
          let color_disciplina=disciplina.nombre_corto?disciplina.nombre_corto:
                               (disciplina.nombre=="BALONCESTO"?"#ed7213":rol_partido.campeonato.configuracion_tarjeta_jugador.fondo_color_tarjeta);
          doc.font('Helvetica',11);
          let ancho_disciplina=Math.max(120,doc.widthOfString(disciplina.nombre)+20);
          doc.roundedRect(306-ancho_disciplina/2,y_partido+55,ancho_disciplina,13,5).fill(color_disciplina).stroke();
          doc.fillColor(rol_partido.campeonato.configuracion_tarjeta_jugador.enunciados_color);
          doc.text(disciplina.nombre,0,y_partido+57.5,{width:612,align:"center"});
          doc.font('Helvetica-Bold',16);
        }
        doc.fillColor(rol_partido.campeonato.configuracion_tarjeta_jugador.enunciados_color);
        doc.text("VS",288,y_partido+34,{width:36,align:"center"});
        doc.fillColor(rol_partido.campeonato.configuracion_tarjeta_jugador.datos_color);
        let fecha_hora=new Date(rol_partido.partidos[i].fecha_hora);
        doc.font('Helvetica-Bold',25);
        if(!con_resultados){
          doc.text(fecha_hora.getHours()+":"+String(fecha_hora.getMinutes()).padStart(2,'0'),271,y_partido+13,{width:70,align:"center"});
        }
        doc.font('Helvetica-Bold',16);
        doc.fillColor(rol_partido.campeonato.configuracion_tarjeta_jugador.enunciados_color);
        doc.text(rol_partido.partidos[i].equipo_2.nombre,326,y_partido+35,{width:266,align:"center"});
        if(con_resultados && rol_partido.partidos[i].marcador_equipo_1!=null){
          doc.circle(256, y_partido+33, 20)
          .lineWidth(3)
          .fillOpacity(0.8)
          .fillAndStroke(rol_partido.campeonato.configuracion_tarjeta_jugador.enunciados_color,rol_partido.campeonato.configuracion_tarjeta_jugador.fondo_color_tarjeta);
          doc.fillColor(rol_partido.campeonato.configuracion_tarjeta_jugador.fondo_color_tarjeta);
          doc.text(rol_partido.partidos[i].marcador_equipo_1,236,y_partido+26,{width:40,align:'center'});
        }
        if(con_resultados && rol_partido.partidos[i].lista_amarillas_equipo_1!=null && rol_partido.partidos[i].lista_amarillas_equipo_1.length>0){
          doc.fillColor("#ffff00");
          doc.rect(90,y_partido+10,13,13).fill("#ffff00").stroke();
          doc.fillColor("#000000");
          doc.text(rol_partido.partidos[i].lista_amarillas_equipo_1.length,90,y_partido+11,{width:13,height:13,align:"center"});
        }
        if(con_resultados && rol_partido.partidos[i].lista_rojas_equipo_1!=null && rol_partido.partidos[i].lista_rojas_equipo_1.length>0){
          doc.fillColor("#ffff00");
          doc.rect(104,y_partido+10,13,13).fill("#ff0000").stroke();
          doc.fillColor("#000000");
          doc.text(rol_partido.partidos[i].lista_rojas_equipo_1.length,104,y_partido+11,{width:13,height:13,align:"center"});
        }
        if(con_resultados && rol_partido.partidos[i].marcador_equipo_2!=null){
          doc.circle(356, y_partido+33, 20)
          .lineWidth(3)
          .fillOpacity(0.8)
          .fillAndStroke(rol_partido.campeonato.configuracion_tarjeta_jugador.enunciados_color,rol_partido.campeonato.configuracion_tarjeta_jugador.fondo_color_tarjeta);
          doc.fillColor(rol_partido.campeonato.configuracion_tarjeta_jugador.fondo_color_tarjeta);
          doc.text(rol_partido.partidos[i].marcador_equipo_2,336,y_partido+26,{width:40,align:'center'});
        }
        if(con_resultados && rol_partido.partidos[i].lista_amarillas_equipo_2!=null && rol_partido.partidos[i].lista_amarillas_equipo_2.length>0){
          doc.fillColor("#ffff00");
          doc.rect(495,y_partido+10,13,13).fill("#ffff00").stroke();
          doc.fillColor("#000000");
          doc.text(rol_partido.partidos[i].lista_amarillas_equipo_2.length,495,y_partido+11,{width:13,height:13,align:"center"});
        }
        if(con_resultados && rol_partido.partidos[i].lista_rojas_equipo_2!=null && rol_partido.partidos[i].lista_rojas_equipo_2.length>0){
          doc.fillColor("#ffff00");
          doc.rect(509,y_partido+10,13,13).fill("#ff0000").stroke();
          doc.fillColor("#000000");
          doc.text(rol_partido.partidos[i].lista_rojas_equipo_2.length,509,y_partido+11,{width:13,height:13,align:"center"});
        }
        doc.fillColor("#000000");
        let logo_eq_2=await this.imagen(this.rest_server+rol_partido.partidos[i].equipo_2.logo);
        doc.image(logo_eq_2,550,y_partido+5, { width: 50, height: 50,align:'right' });
      }else{
        doc.roundedRect(153,y_partido+15,300,30,5).fill(rol_partido.campeonato.configuracion_tarjeta_jugador.fondo_color_tarjeta).stroke();
        let fecha_hora=new Date(rol_partido.partidos[i].fecha_hora);
        doc.fillColor(rol_partido.campeonato.configuracion_tarjeta_jugador.enunciados_color);
        let y_fecha=y_partido+23;
        if(rol_partido.campeonato.configuracion_pdf_rol_partido.mostrar_escenario && !con_resultados){
          y_fecha=y_partido+20;
          doc.font('Helvetica-Bold',10);
          doc.text(rol_partido.partidos[i].escenario.nombre,0,y_partido+35,{width:612,align:"center"});
          doc.font('Helvetica-Bold',16);
        }
        doc.text(fecha_hora.toLocaleDateString("es-ES", { weekday: 'long' }).toUpperCase()+", "+fecha_hora.getDate()+" "+fecha_hora.toLocaleString('es-ES', { month: 'long' }).toUpperCase()+" "+fecha_hora.getFullYear(),0,y_fecha,{width:612,align:"center"});
      }
      y_partido=y_partido+rol_partido.campeonato.configuracion_pdf_rol_partido.distancia_partidos;

      if(items==partidos_por_pagina){
        items=0;
        y_partido=100;
        doc.addPage({size:[612, Math.max(150+(rol_partido.campeonato.configuracion_pdf_rol_partido.distancia_partidos*rol_partido.partidos.length),468)],margin:10});
        page++;
        await this.dibujarCabeceraRolPartido(doc,fondo,rol_partido,con_resultados);
      }
    }
    return { doc:doc, stream:stream };
  }

  /* PDFKit no incrusta las fuentes estandar: el PDF solo las nombra y cada visor pone la suya. Al
     convertir el PDF a PNG en el servidor, pdf.js las reemplaza por sus sustitutas y las letras
     salen con otro dibujo. Incrustando LiberationSans el archivo lleva sus propios glifos y se ve
     igual en todos lados; es metricamente identica a Helvetica, asi que la maquetacion no cambia.
     Si la descarga falla, el fixture sigue saliendo con las fuentes estandar. */
  async prepararFuentesFixture(doc){
    this.fuente_fixture="Helvetica";
    this.fuente_fixture_negrita="Helvetica-Bold";
    try{
      if(!this.fuentes_fixture){
        let normal=await fetch("assets/fonts/LiberationSans-Regular.ttf").then(respuesta => respuesta.arrayBuffer());
        let negrita=await fetch("assets/fonts/LiberationSans-Bold.ttf").then(respuesta => respuesta.arrayBuffer());
        this.fuentes_fixture={ normal:new Uint8Array(normal), negrita:new Uint8Array(negrita) };
      }
      doc.registerFont("FixtureSans",this.fuentes_fixture.normal);
      doc.registerFont("FixtureSansBold",this.fuentes_fixture.negrita);
      this.fuente_fixture="FixtureSans";
      this.fuente_fixture_negrita="FixtureSansBold";
    }catch(error){
      console.warn("No se pudieron incrustar las fuentes del fixture, se usaran las estandar",error);
    }
  }

  /* ─── Helpers de estilo del fixture V2 ─────────────────────────────────────────
     Los tonos suaves y oscuros se derivan del color del campeonato, asi el PDF se ve armado sin
     pedirle al usuario que configure colores nuevos. */

  aclararColorPdf(color,factor){
    let hex=this.normalizarColorPdf(color);
    let canales=[0,2,4].map(i => parseInt(hex.substr(i,2),16));
    return "#"+canales.map(v => Math.round(v+(255-v)*factor).toString(16).padStart(2,'0')).join('');
  }

  oscurecerColorPdf(color,factor){
    let hex=this.normalizarColorPdf(color);
    let canales=[0,2,4].map(i => parseInt(hex.substr(i,2),16));
    return "#"+canales.map(v => Math.round(v*(1-factor)).toString(16).padStart(2,'0')).join('');
  }

  normalizarColorPdf(color){
    let hex=(color?String(color):"").replace('#','');
    if(hex.length==3){
      hex=hex[0]+hex[0]+hex[1]+hex[1]+hex[2]+hex[2];
    }
    return /^[0-9a-fA-F]{6}$/.test(hex)?hex:"777777";
  }

  esColorPdf(valor){
    return !!valor && String(valor).charAt(0)=="#";
  }

  /* Las categorias y disciplinas guardan su color en nombre_corto. Se usa tanto en la lista como
     en las tarjetas del calendario, para que cada una se lea siempre del mismo color. */
  colorClase(clase){
    return (clase && this.esColorPdf(clase.nombre_corto))?clase.nombre_corto:null;
  }

  luminanciaPdf(color){
    let hex=this.normalizarColorPdf(color);
    let canales=[0,2,4].map(i => parseInt(hex.substr(i,2),16)/255)
                       .map(valor => (valor<=0.04045)?(valor/12.92):Math.pow((valor+0.055)/1.055,2.4));
    return 0.2126*canales[0]+0.7152*canales[1]+0.0722*canales[2];
  }

  /* Cuanto hay que oscurecer el color del campeonato para que el texto blanco quede legible sobre
     la tarjeta translucida. Se cuenta el peor caso: un pixel blanco de los neones de la cancha,
     ya atenuado por el tinte y por la opacidad de la tarjeta. Los colores oscuros casi no se
     tocan; los claros (cian, celeste) se oscurecen solo lo necesario. */
  oscurecimientoTarjetaFixture(base){
    let luminancia_fondo=1.0*(1-this.intensidad_tinte_fixture)+
                         this.luminanciaPdf(this.oscurecerColorPdf(base,0.55))*this.intensidad_tinte_fixture;
    for(let factor=0;factor<=0.9;factor=factor+0.02){
      let luminancia=luminancia_fondo*(1-this.opacidad_tarjeta_fixture)+
                     this.luminanciaPdf(this.oscurecerColorPdf(base,factor))*this.opacidad_tarjeta_fixture;
      if((1.05/(luminancia+0.05))>=4.8){
        return factor;
      }
    }
    return 0.9;
  }

  /* La tarjeta se dibuja como PNG con canal alfa: es la unica forma de lograr transparencia real,
     porque este build de PDFKit ignora doc.opacity(). PDFKit la incrusta con mascara suave y la
     cancha se ve a traves. Se genera una sola vez por documento. */
  tarjetaFixturePng(ancho,alto,color,alfa,radio){
    let escala=3;
    let lienzo=document.createElement('canvas');
    lienzo.width=ancho*escala; lienzo.height=alto*escala;
    let contexto=lienzo.getContext('2d');
    contexto.scale(escala,escala);
    contexto.globalAlpha=alfa;
    contexto.fillStyle=color;
    contexto.beginPath();
    contexto.moveTo(radio,0);
    contexto.lineTo(ancho-radio,0);
    contexto.quadraticCurveTo(ancho,0,ancho,radio);
    contexto.lineTo(ancho,alto-radio);
    contexto.quadraticCurveTo(ancho,alto,ancho-radio,alto);
    contexto.lineTo(radio,alto);
    contexto.quadraticCurveTo(0,alto,0,alto-radio);
    contexto.lineTo(0,radio);
    contexto.quadraticCurveTo(0,0,radio,0);
    contexto.closePath();
    contexto.fill();
    return lienzo.toDataURL('image/png');
  }

  /* Tiñe la imagen de fondo con el color del campeonato y la deja del tamaño exacto de la pagina.
     Se hornea en un canvas porque este build de PDFKit ignora doc.opacity() y los degradados con
     alfa, asi que no hay forma de superponer un velo translucido dentro del PDF.
     La pagina crece con la cantidad de partidos, asi que la cancha se repite hacia abajo en vez de
     estirarse: con un rol largo, estirarla la deformaba por completo.
     Si algo falla, devuelve la imagen original. */
  tenirImagenPdf(imagen_base64,color,intensidad,ancho,alto){
    return new Promise((resolver) => {
      try{
        let imagen=new Image();
        imagen.onload=() => {
          try{
            let lienzo=document.createElement('canvas');
            lienzo.width=ancho; lienzo.height=alto;
            let contexto=lienzo.getContext('2d');
            let alto_baldosa=Math.round(ancho*(imagen.height/imagen.width));
            for(let y=0;y<alto;y=y+alto_baldosa){
              contexto.drawImage(imagen,0,y,ancho,alto_baldosa);
            }
            contexto.globalAlpha=intensidad;
            contexto.fillStyle=color;
            contexto.fillRect(0,0,ancho,alto);
            resolver(lienzo.toDataURL('image/jpeg',0.86));
          }catch(error){
            resolver(imagen_base64);
          }
        };
        imagen.onerror=() => resolver(imagen_base64);
        imagen.src=imagen_base64;
      }catch(error){
        resolver(imagen_base64);
      }
    });
  }

  horaTextoPdf(fecha){
    let hora=fecha.getHours(),minuto=fecha.getMinutes();
    return (hora<10?"0":"")+hora+":"+(minuto<10?"0":"")+minuto;
  }

  //cabecera propia del fixture V2, para no alterar la de las versiones 1 y 3
  async dibujarCabeceraRolPartidoV2(doc,fondo,rol_partido,con_resultados){
    this.asegurarConfiguracionPdfRolPartido(rol_partido.campeonato);
    let colores=rol_partido.campeonato.configuracion_tarjeta_jugador;
    let base=colores.fondo_color_tarjeta;

    //la cancha llega ya tenida con el color del campeonato desde tenirImagenPdf
    doc.image(fondo,0,0,{ width:this.ancho_fixture, height:doc.page.height });

    doc.rect(0,0,612,96).fill(base);
    doc.rect(0,96,612,3).fill(colores.datos_color?colores.datos_color:this.aclararColorPdf(base,0.5));

    let logo=await this.imagen(this.rest_server+rol_partido.campeonato.logo);
    if(rol_partido.campeonato.configuracion_pdf_rol_partido.campeonato_propio){
      if(this.usuario.empresa.imagen && this.usuario.empresa.imagen.length>100){
        doc.image(this.usuario.empresa.imagen,24,14,{ width:68, height:68 });
      }
      doc.fillColor(colores.enunciados_color);
      doc.font(this.fuente_fixture_negrita,7);
      doc.text("PERSONERÍA JURÍDICA",14,80,{ width:88, align:'center', characterSpacing:0.3, lineBreak:false });
      doc.text("N° 0157",14,88,{ width:88, align:'center', characterSpacing:0.3, lineBreak:false });
    }else{
      doc.image(logo,24,14,{ width:68, height:68 });
    }
    doc.image(logo,520,14,{ width:68, height:68 });

    /* el nombre del campeonato manda: se lleva todo el ancho entre los dos logos y solo se achica
       lo necesario para entrar en dos lineas */
    doc.fillColor(colores.enunciados_color);
    let nombre_campeonato=rol_partido.campeonato.nombre.toUpperCase();
    this.tamanoBloqueFixture(doc,nombre_campeonato,this.fuente_fixture_negrita,26,400,46,1.2,11);
    let lineas_campeonato=this.armarLineasFixture(doc,nombre_campeonato,400,1.2);
    let alto_linea_campeonato=doc.currentLineHeight(true);
    this.dibujarLineasFixture(doc,lineas_campeonato,106,10+(46-lineas_campeonato.length*alto_linea_campeonato)/2,
                              400,'center',1.2,alto_linea_campeonato);

    let titulo=(con_resultados?"RESULTADOS · ":"")+rol_partido.rol.nombre.toUpperCase();
    let tamano_titulo=this.tamanoLineaFixture(doc,titulo,this.fuente_fixture_negrita,13,360,1.6,8);
    let ancho_titulo=doc.widthOfString(titulo,{ characterSpacing:1.6 })+30;
    let x_titulo=(612-ancho_titulo)/2;
    doc.roundedRect(x_titulo,60,ancho_titulo,26,13).fill(this.oscurecerColorPdf(base,0.45));
    doc.fillColor("#ffffff");
    doc.text(titulo,x_titulo,60+(26-tamano_titulo)/2,{ width:ancho_titulo, align:'center', characterSpacing:1.6, lineBreak:false });
  }

  dibujarSeparadorFechaV2(doc,rol_partido,partido,y,plan){
    let colores=rol_partido.campeonato.configuracion_tarjeta_jugador;
    let fecha_hora=new Date(partido.fecha_hora);
    let escala=plan.escala;
    let hay_escenario=rol_partido.campeonato.configuracion_pdf_rol_partido.mostrar_escenario && partido.escenario;
    //fecha y cancha centradas, una debajo de la otra; sin cancha la barra es de una sola linea
    let alto_barra=(hay_escenario?50:34)*escala;
    let y_barra=y+(plan.alto_tarjeta-alto_barra)/2;

    //sobre el fondo tenido, la barra va en el color pleno del campeonato para que resalte
    doc.roundedRect(20,y_barra,572,alto_barra,alto_barra/5).fill(colores.fondo_color_tarjeta);
    doc.fillColor("#ffffff");
    let texto_fecha=fecha_hora.toLocaleDateString("es-ES",{ weekday:'long' }).toUpperCase()+" "+fecha_hora.getDate()+
                    " DE "+fecha_hora.toLocaleString("es-ES",{ month:'long' }).toUpperCase()+" "+fecha_hora.getFullYear();
    let tamano_fecha=this.tamanoLineaFixture(doc,texto_fecha,this.fuente_fixture_negrita,15*escala,544,1.4,8);
    let alto_fecha=tamano_fecha*1.2;
    let escenario="",tamano_escenario=0,alto_escenario=0;
    if(hay_escenario){
      escenario=partido.escenario.nombre.toUpperCase();
      tamano_escenario=this.tamanoLineaFixture(doc,escenario,this.fuente_fixture,11*escala,544,0.6,7);
      alto_escenario=tamano_escenario*1.4;
    }

    //las dos lineas van centradas como un solo bloque dentro de la barra
    let y_texto=y_barra+(alto_barra-(alto_fecha+alto_escenario))/2;
    doc.font(this.fuente_fixture_negrita,tamano_fecha);
    doc.text(texto_fecha,34,y_texto,{ width:544, align:'center', characterSpacing:1.4, lineBreak:false });
    if(hay_escenario){
      doc.font(this.fuente_fixture,tamano_escenario);
      doc.text(escenario,34,y_texto+alto_fecha,
               { width:544, align:'center', characterSpacing:0.6, lineBreak:false, ellipsis:true });
    }
  }

  async dibujarPartidoV2(doc,rol_partido,partido,y,con_resultados,plan){
    let colores=rol_partido.campeonato.configuracion_tarjeta_jugador;
    let base=colores.fondo_color_tarjeta;
    let escala=plan.escala,escala_h=plan.escala_horizontal,alto=plan.alto_tarjeta;
    //el nombre_corto de la categoria guarda el color; el walk over manda sobre el
    let color_categoria=this.esColorPdf(partido.categoria?partido.categoria.nombre_corto:null)?partido.categoria.nombre_corto:base;
    if(con_resultados && ((partido.w_o_equipo_1 && partido.w_o_equipo_1.es_wo) || (partido.w_o_equipo_2 && partido.w_o_equipo_2.es_wo))){
      color_categoria="#ed5f07";
    }

    /* Tarjeta translucida: va como PNG con canal alfa, asi la cancha se ve a traves. El PNG se
       arma una sola vez por documento. Sin sombra, porque se transparentaria. */
    if(!this.tarjeta_fixture_png){
      this.tarjeta_fixture_png=this.tarjetaFixturePng(572,alto,
        this.oscurecerColorPdf(base,this.oscurecimientoTarjetaFixture(base)),this.opacidad_tarjeta_fixture,9*escala_h);
    }
    doc.image(this.tarjeta_fixture_png,20,y,{ width:572, height:alto });
    doc.lineWidth(1);
    doc.roundedRect(20,y,572,alto,9*escala_h).stroke(this.aclararColorPdf(base,0.25));
    //franja de categoria pegada al borde izquierdo, recortada al radio de la tarjeta
    doc.save();
    doc.roundedRect(20,y,572,alto,9*escala_h).clip();
    doc.rect(20,y,7*escala_h,alto).fill(color_categoria);
    doc.restore();

    /* Reparto horizontal: logos y bloque central crecen amortiguados y lo que sobra queda para los
       nombres, que son lo que la gente lee de lejos. */
    let logo=52*escala_h;
    let ancho_centro=118*escala_h;
    let x_logo_1=32,x_logo_2=580-logo;
    let x_nombre_1=x_logo_1+logo+10;
    let ancho_nombre=(306-ancho_centro/2-10)-x_nombre_1;
    let x_nombre_2=306+ancho_centro/2+10;

    let logo_eq_1=await this.imagen(this.rest_server+partido.equipo_1.logo);
    let logo_eq_2=await this.imagen(this.rest_server+partido.equipo_2.logo);
    doc.image(logo_eq_1,x_logo_1,y+(alto-logo)/2,{ width:logo, height:logo });
    doc.image(logo_eq_2,x_logo_2,y+(alto-logo)/2,{ width:logo, height:logo });

    //los nombres se centran verticalmente segun cuantas lineas ocupen
    doc.fillColor("#ffffff");
    let nombre_1=partido.equipo_1.nombre.toUpperCase(),nombre_2=partido.equipo_2.nombre.toUpperCase();
    //los dos van del mismo tamaño: el que le entra al mas largo
    let tamano_nombre=Math.min(
      this.tamanoBloqueFixture(doc,nombre_1,this.fuente_fixture_negrita,15*escala,ancho_nombre,alto*0.78,0.2,8),
      this.tamanoBloqueFixture(doc,nombre_2,this.fuente_fixture_negrita,15*escala,ancho_nombre,alto*0.78,0.2,8));
    doc.font(this.fuente_fixture_negrita,tamano_nombre);
    let lineas_1=this.armarLineasFixture(doc,nombre_1,ancho_nombre,0.2);
    let lineas_2=this.armarLineasFixture(doc,nombre_2,ancho_nombre,0.2);
    let alto_linea=doc.currentLineHeight(true);
    this.dibujarLineasFixture(doc,lineas_1,x_nombre_1,y+(alto-lineas_1.length*alto_linea)/2,ancho_nombre,'right',0.2,alto_linea);
    this.dibujarLineasFixture(doc,lineas_2,x_nombre_2,y+(alto-lineas_2.length*alto_linea)/2,ancho_nombre,'left',0.2,alto_linea);

    if(con_resultados && partido.marcador_equipo_1!=null && partido.marcador_equipo_2!=null){
      //sobre la tarjeta oscura el marcador va en el color de datos, que es el que mas vibra
      doc.font(this.fuente_fixture_negrita,27*escala);
      doc.fillColor(colores.datos_color?colores.datos_color:this.aclararColorPdf(base,0.4));
      doc.text(String(partido.marcador_equipo_1),306-54*escala_h,y+alto*0.20,{ width:44*escala_h, align:'right' });
      doc.text(String(partido.marcador_equipo_2),306+10*escala_h,y+alto*0.20,{ width:44*escala_h, align:'left' });
      doc.font(this.fuente_fixture_negrita,20*escala);
      doc.fillColor(this.aclararColorPdf(base,0.25));
      doc.text("-",306-10*escala_h,y+alto*0.24,{ width:20*escala_h, align:'center' });
      doc.font(this.fuente_fixture,10*escala);
      doc.fillColor(this.aclararColorPdf(base,0.55));
      doc.text(this.horaTextoPdf(new Date(partido.fecha_hora)),256,y+alto*0.60,{ width:100, align:'center', characterSpacing:0.8 });
    }else{
      let ancho_pastilla=96*escala_h,alto_pastilla=30*escala;
      doc.roundedRect(306-ancho_pastilla/2,y+alto*0.20,ancho_pastilla,alto_pastilla,alto_pastilla/2).fill(base);
      doc.fillColor(colores.enunciados_color);
      doc.font(this.fuente_fixture_negrita,19*escala);
      doc.text(this.horaTextoPdf(new Date(partido.fecha_hora)),306-ancho_pastilla/2,y+alto*0.20+(alto_pastilla-19*escala)/2+1,
               { width:ancho_pastilla, align:'center', characterSpacing:0.8 });
    }

    //las etiquetas no pueden pasarse del bloque central, o se meten encima de los nombres
    let ancho_maximo_etiqueta=ancho_centro+6;
    //disciplina arriba del todo, para no pelear el espacio con la hora y la categoria
    if(rol_partido.campeonato.configuracion_pdf_rol_partido.mostrar_disciplina && partido.disciplina){
      let color_disciplina=this.esColorPdf(partido.disciplina.nombre_corto)?partido.disciplina.nombre_corto:this.oscurecerColorPdf(base,0.2);
      let etiqueta_disciplina=partido.disciplina.nombre.toUpperCase();
      let tamano_disciplina=this.tamanoLineaFixture(doc,etiqueta_disciplina,this.fuente_fixture_negrita,7.5*escala,
                                                    ancho_maximo_etiqueta-16*escala_h,0.8,5);
      let ancho_disciplina=doc.widthOfString(etiqueta_disciplina,{ characterSpacing:0.8 })+16*escala_h;
      let alto_disciplina=15*escala;
      doc.roundedRect(306-ancho_disciplina/2,y+alto*0.027,ancho_disciplina,alto_disciplina,alto_disciplina/2).fill(color_disciplina);
      doc.fillColor("#ffffff");
      doc.text(etiqueta_disciplina,306-ancho_disciplina/2,y+alto*0.027+(alto_disciplina-tamano_disciplina)/2,
               { width:ancho_disciplina, align:'center', characterSpacing:0.8, lineBreak:false });
    }

    if(rol_partido.campeonato.configuracion_pdf_rol_partido.mostrar_categoria && partido.categoria){
      let etiqueta=partido.categoria.nombre.toUpperCase();
      let tamano_etiqueta=this.tamanoLineaFixture(doc,etiqueta,this.fuente_fixture_negrita,8.5*escala,
                                                  ancho_maximo_etiqueta-18*escala_h,1,5);
      let ancho=doc.widthOfString(etiqueta,{ characterSpacing:1 })+18*escala_h;
      let alto_etiqueta=16*escala;
      let y_etiqueta=y+alto*(con_resultados?0.77:0.635);
      doc.roundedRect(306-ancho/2,y_etiqueta,ancho,alto_etiqueta,alto_etiqueta/2).fill(color_categoria);
      doc.fillColor("#ffffff");
      doc.text(etiqueta,306-ancho/2,y_etiqueta+(alto_etiqueta-tamano_etiqueta)/2,
               { width:ancho, align:'center', characterSpacing:1, lineBreak:false });
    }

    if(con_resultados){
      this.dibujarTarjetasV2(doc,partido.lista_amarillas_equipo_1,partido.lista_rojas_equipo_1,x_nombre_1-4,y+alto*0.12,'derecha',plan);
      this.dibujarTarjetasV2(doc,partido.lista_amarillas_equipo_2,partido.lista_rojas_equipo_2,x_nombre_2+ancho_nombre+4,y+alto*0.12,'izquierda',plan);
    }
  }

  dibujarTarjetasV2(doc,amarillas,rojas,x,y,lado,plan){
    let escala=plan.escala,desplazamiento=0;
    let ancho=12*escala,alto=15*escala;
    doc.font(this.fuente_fixture_negrita,8*escala);
    let pintar=(lista,color_fondo,color_texto) => {
      let cantidad=(lista && lista.length)?lista.length:0;
      if(cantidad==0){
        return;
      }
      let x_tarjeta=(lado=='derecha')?(x-desplazamiento-ancho):(x+desplazamiento);
      doc.roundedRect(x_tarjeta,y,ancho,alto,2*escala).fill(color_fondo);
      doc.fillColor(color_texto);
      doc.text(String(cantidad),x_tarjeta,y+(alto-8*escala)/2+1,{ width:ancho, align:'center' });
      desplazamiento=desplazamiento+ancho+3*escala;
    };
    pintar(amarillas,"#f2c200","#3a2f00");
    pintar(rojas,"#d32f2f","#ffffff");
  }

  /* alto_fondo: la version 1 manda el fondo ya armado al alto de su hoja mas larga; sin el, el fondo
     se estira al tamaño oficio como siempre (version 3). */
  async dibujarCabeceraRolPartido(doc,fondo,rol_partido,con_resultados,alto_fondo?){
    this.asegurarConfiguracionPdfRolPartido(rol_partido.campeonato);
    doc.opacity(0.5);
    doc.image(fondo,0,0, { width: GlobalVariable.Dictionary.PAPEL_OFICIO_MEDIDA[0], height: alto_fondo?alto_fondo:GlobalVariable.Dictionary.PAPEL_OFICIO_MEDIDA[1],align:'right' });
    doc.opacity(1.0);
    doc.rect(0,0,GlobalVariable.Dictionary.PAPEL_OFICIO_MEDIDA[0],80).fill(rol_partido.campeonato.configuracion_tarjeta_jugador.fondo_color_tarjeta).stroke();
    //un campeonato con el logo roto ya no corta el PDF: se usa el logo por defecto
    let logo=await this.imagen(this.rest_server+rol_partido.campeonato.logo)
                       .catch(() => this.imagen(this.rest_server+"./img/icon-campeonato-default.png"));
    doc.fillColor(rol_partido.campeonato.configuracion_tarjeta_jugador.enunciados_color);
    if(rol_partido.campeonato.configuracion_pdf_rol_partido.campeonato_propio){
      /* La personeria va debajo del logo de la empresa, en dos renglones, como en la version 2. El
         logo se achica un poco para que los dos entren en la franja de 80 pt. */
      if (this.usuario.empresa.imagen && this.usuario.empresa.imagen.length > 100) {
        doc.image(this.usuario.empresa.imagen,26,3, { width: 58, height: 58,align:'left' });
      }
      doc.font('Times-Bold',7);
      doc.text("PERSONERÍA JURÍDICA",10,63,{ width:90, align:'center', characterSpacing:0.3, lineBreak:false });
      doc.text("N° 0157",10,71,{ width:90, align:'center', characterSpacing:0.3, lineBreak:false });
    }else{
      doc.image(logo,20,5, { width: 70, height: 70,align:'left' });
    }
    /* El titulo va solo en el medio, sin pisar los logos (hasta 100 y desde 522). El ancho es parejo
       a los dos lados para que quede centrado en la hoja, y el nombre se achica o se parte en dos
       lineas hasta que entra. */
    let x_titulo=100;
    let ancho_titulo=612-2*x_titulo;
    let nombre_campeonato=rol_partido.campeonato.nombre.toUpperCase();
    this.tamanoBloqueFixture(doc,nombre_campeonato,'Times-Bold',30,ancho_titulo,40,0,12);
    let lineas_campeonato=this.armarLineasFixture(doc,nombre_campeonato,ancho_titulo,0);
    let alto_linea_campeonato=doc.currentLineHeight(true);
    this.dibujarLineasFixture(doc,lineas_campeonato,x_titulo,12+(40-lineas_campeonato.length*alto_linea_campeonato)/2,
                              ancho_titulo,'center',0,alto_linea_campeonato);
    let subtitulo=(con_resultados?"RESULTADOS ":"")+rol_partido.rol.nombre;
    this.tamanoLineaFixture(doc,subtitulo,'Times-Bold',15,ancho_titulo,0,9);
    doc.text(subtitulo,x_titulo,52,{width:ancho_titulo,align:"center",lineBreak:false});
    doc.image(logo,522,5, { width: 70, height: 70,align:'right' });
    doc.font('Helvetica-Bold',16);
  }
}
